import { createSlice } from '@reduxjs/toolkit';
import { EmployeeSalaryStructureState } from './employeeSalaryStructureTypes';
import {
  createEmployeeSalaryStructure,
  fetchEmployeeSalaryStructures,
  fetchEmployeeSalaryStructureById,
  updateEmployeeSalaryStructure,
  deleteEmployeeSalaryStructure,
  fetchAllEmployeeLeaveData,
  fetchSalaryStructureByEmployeeId,
  fetchAllSalaryStructureLogs,
  fetchSalaryStructureLogsByEmployeeId
} from './employeeSalaryStructureActions';

const initialState: EmployeeSalaryStructureState = {
  salaryStructures: [],
  salryCalculation: [],
  selectedSalaryStructure: undefined,
  employeeSalaryStructures: [],
  salaryStructureLogs: [],
  employeeSalaryStructureLogs: [],
  loading: false,
  error: undefined,
};

const employeeSalaryStructureSlice = createSlice({
  name: 'employeeSalaryStructure',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Create Employee Salary Structure
      .addCase(createEmployeeSalaryStructure.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(createEmployeeSalaryStructure.fulfilled, (state, action) => {
        state.loading = false;
        state.salaryStructures.push(action.payload);
      })
      .addCase(createEmployeeSalaryStructure.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch All Employee Salary Structures
      .addCase(fetchEmployeeSalaryStructures.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchEmployeeSalaryStructures.fulfilled, (state, action) => {
        state.loading = false;
        state.salaryStructures = action.payload.data;
      })
      .addCase(fetchEmployeeSalaryStructures.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch Employee Salary Structure by ID
      .addCase(fetchEmployeeSalaryStructureById.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchEmployeeSalaryStructureById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedSalaryStructure = action.payload;
      })
      .addCase(fetchEmployeeSalaryStructureById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch Salary Structure by Employee ID
      .addCase(fetchSalaryStructureByEmployeeId.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchSalaryStructureByEmployeeId.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeSalaryStructures = action.payload.data;
      })
      .addCase(fetchSalaryStructureByEmployeeId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update Employee Salary Structure
      .addCase(updateEmployeeSalaryStructure.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(updateEmployeeSalaryStructure.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.salaryStructures.findIndex(structure => structure.id === action.payload.id);
        if (index !== -1) {
          state.salaryStructures[index] = action.payload;
        }
      })
      .addCase(updateEmployeeSalaryStructure.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Delete Employee Salary Structure
      .addCase(deleteEmployeeSalaryStructure.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(deleteEmployeeSalaryStructure.fulfilled, (state, action) => {
        state.loading = false;
        state.salaryStructures = state.salaryStructures.filter(structure => structure.id !== action.payload.id);
      })
      .addCase(deleteEmployeeSalaryStructure.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch All Employee salary calculation Data
      .addCase(fetchAllEmployeeLeaveData.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchAllEmployeeLeaveData.fulfilled, (state, action) => {
        state.loading = false;
        state.salryCalculation = action.payload.data;
      })
      .addCase(fetchAllEmployeeLeaveData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // All logs
      .addCase(fetchAllSalaryStructureLogs.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchAllSalaryStructureLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.salaryStructureLogs = action.payload.data;
      })
      .addCase(fetchAllSalaryStructureLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Logs by employeeId
      .addCase(fetchSalaryStructureLogsByEmployeeId.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchSalaryStructureLogsByEmployeeId.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeSalaryStructureLogs = action.payload.data; // API returns { data: [...] }
      })
      .addCase(fetchSalaryStructureLogsByEmployeeId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default employeeSalaryStructureSlice.reducer;
